import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';
import {
  PagedResult,
  POrderHistoryDto,
  POrderHistoryQueryParams,
  ReceivedPurchaseOrderDto,
  ReceivedPurchaseOrderQueryParams
} from '../types/purchaseOrderHistory';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/**
 * Service for managing Purchase Order History (Bestilling Historikk)
 * Provides methods for fetching hierarchical order history with lines and transactions
 *
 * This service handles the complete purchase order history including:
 * - Order header information
 * - Nested order lines with delivery details
 * - Stock transactions for each line showing warehouse activities
 *
 * All data is tenant-aware and automatically filtered by company context.
 */
class PurchaseOrderHistoryService {

  /**
   * Get paginated purchase order history with nested lines and transactions
   * Supports server-side filtering and pagination
   *
   * @param params Query parameters for pagination and filtering
   * @returns Promise with paginated result containing order history
   * @throws Error if the request fails
   *
   * @example
   * // Get history for last 30 days
   * const result = await purchaseOrderHistoryService.getOrderHistory({
   *   fromDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
   *   toDate: new Date(),
   *   pageNumber: 1,
   *   pageSize: 20
   * });
   *
   * @example
   * // Get history for specific supplier
   * const result = await purchaseOrderHistoryService.getOrderHistory({
   *   supplierNo: 'SUP001',
   *   fromDate: '2025-01-01',
   *   toDate: '2025-01-31'
   * });
   *
   * @example
   * // Search by ERP order number
   * const result = await purchaseOrderHistoryService.getOrderHistory({
   *   orderNoERP: 'PO-2025-001'
   * });
   */
  async getOrderHistory(
    params: POrderHistoryQueryParams = {}
  ): Promise<PagedResult<POrderHistoryDto>> {
    // Build query string from parameters
    const queryParams = new URLSearchParams();

    // Pagination
    if (params.pageNumber !== undefined) {
      queryParams.append('pageNumber', params.pageNumber.toString());
    }
    if (params.pageSize !== undefined) {
      queryParams.append('pageSize', params.pageSize.toString());
    }

    // Date filtering
    if (params.fromDate) {
      const fromDateStr = typeof params.fromDate === 'string'
        ? params.fromDate
        : this.formatDateForApi(params.fromDate);
      queryParams.append('fromDate', fromDateStr);
    }
    if (params.toDate) {
      const toDateStr = typeof params.toDate === 'string'
        ? params.toDate
        : this.formatDateForApi(params.toDate);
      queryParams.append('toDate', toDateStr);
    }

    // Additional filters
    if (params.supplierNo) {
      queryParams.append('supplierNo', params.supplierNo);
    }
    if (params.orderNoERP) {
      queryParams.append('orderNoERP', params.orderNoERP);
    }

    // Build final URL
    const url = `${API_BASE_URL}/porders/history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    // Make authenticated request
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to get purchase order history'));
    }

    return await response.json();
  }

  /**
   * Get purchase order history for the last N days
   * Convenient method for fetching recent history
   *
   * @param days Number of days to look back (default: 30)
   * @param additionalParams Additional query parameters
   * @returns Promise with paginated result
   *
   * @example
   * // Get history for last 7 days
   * const result = await purchaseOrderHistoryService.getRecentHistory(7);
   *
   * @example
   * // Get history for last 90 days for specific supplier
   * const result = await purchaseOrderHistoryService.getRecentHistory(90, {
   *   supplierNo: 'SUP001',
   *   pageSize: 50
   * });
   */
  /**
   * Mottatte innkjøp (TW 36403717): innkjøpsordre med minst ett mottak, valgt på siste mottaksdato.
   * Standard i backend: siste 14 dager. Fanen «Innkjøp» i Historikk-modulen.
   */
  async getReceivedHistory(params: ReceivedPurchaseOrderQueryParams = {}): Promise<ReceivedPurchaseOrderDto[]> {
    const queryParams = new URLSearchParams();
    if (params.fromDate) queryParams.append('fromDate', params.fromDate);
    if (params.toDate) queryParams.append('toDate', params.toDate);
    if (params.warehouse) queryParams.append('warehouse', params.warehouse);
    if (params.search && params.search.trim()) queryParams.append('search', params.search.trim());

    const url = `${API_BASE_URL}/porders/received-history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await AuthService.authenticatedGet(url);
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to get received purchase orders'));
    }
    return await response.json();
  }

  async getRecentHistory(
    days: number = 30,
    additionalParams: Omit<POrderHistoryQueryParams, 'fromDate' | 'toDate'> = {}
  ): Promise<PagedResult<POrderHistoryDto>> {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - days);

    return this.getOrderHistory({
      ...additionalParams,
      fromDate,
      toDate
    });
  }

  /**
   * Get purchase order history by supplier number
   * Shorthand method for filtering by supplier
   *
   * @param supplierNo Supplier number to filter by
   * @param additionalParams Additional query parameters
   * @returns Promise with paginated result
   *
   * @example
   * const result = await purchaseOrderHistoryService.getBySupplier('SUP001', {
   *   fromDate: '2025-01-01',
   *   toDate: '2025-01-31'
   * });
   */
  async getBySupplier(
    supplierNo: string,
    additionalParams: Omit<POrderHistoryQueryParams, 'supplierNo'> = {}
  ): Promise<PagedResult<POrderHistoryDto>> {
    return this.getOrderHistory({
      ...additionalParams,
      supplierNo
    });
  }

  /**
   * Get purchase order history within a date range
   * Shorthand method for filtering by date range
   *
   * @param fromDate Start date (inclusive)
   * @param toDate End date (inclusive)
   * @param additionalParams Additional query parameters
   * @returns Promise with paginated result
   *
   * @example
   * const result = await purchaseOrderHistoryService.getByDateRange(
   *   new Date('2025-01-01'),
   *   new Date('2025-01-31'),
   *   { pageSize: 50 }
   * );
   */
  async getByDateRange(
    fromDate: Date | string,
    toDate: Date | string,
    additionalParams: Omit<POrderHistoryQueryParams, 'fromDate' | 'toDate'> = {}
  ): Promise<PagedResult<POrderHistoryDto>> {
    return this.getOrderHistory({
      ...additionalParams,
      fromDate,
      toDate
    });
  }

  /**
   * Search purchase order history by ERP order number
   * Shorthand method for searching by order number (partial match)
   *
   * @param orderNoERP ERP order number to search for (partial match)
   * @param additionalParams Additional query parameters
   * @returns Promise with paginated result
   *
   * @example
   * const result = await purchaseOrderHistoryService.searchByOrderNo('PO-2025');
   */
  async searchByOrderNo(
    orderNoERP: string,
    additionalParams: Omit<POrderHistoryQueryParams, 'orderNoERP'> = {}
  ): Promise<PagedResult<POrderHistoryDto>> {
    return this.getOrderHistory({
      ...additionalParams,
      orderNoERP
    });
  }

  /**
   * Format Date object to API-compatible date string (YYYY-MM-DD)
   *
   * @param date Date object to format
   * @returns ISO date string (YYYY-MM-DD)
   */
  formatDateForApi(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  /**
   * Format date to Norwegian locale (DD.MM.YYYY)
   *
   * @param date Date string or null/undefined
   * @returns Formatted date string or '-'
   */
  formatDate(date: string | null | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('no-NO');
  }

  /**
   * Format date and time to Norwegian locale (DD.MM.YYYY HH:MM:SS)
   *
   * @param date Date string or null/undefined
   * @returns Formatted date/time string or '-'
   */
  formatDateTime(date: string | null | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleString('no-NO');
  }

  /**
   * Format date and time to short format (DD.MM.YYYY HH:MM)
   *
   * @param date Date string or null/undefined
   * @returns Formatted date/time string or '-'
   */
  formatDateTimeShort(date: string | null | undefined): string {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleString('no-NO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Format currency value
   *
   * @param value Numeric value or null/undefined
   * @param currency Currency code (default: 'NOK')
   * @returns Formatted currency string
   */
  formatCurrency(value: number | null | undefined, currency: string = 'NOK'): string {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: currency
    }).format(value);
  }

  /**
   * Format quantity value
   *
   * @param value Numeric value or null/undefined
   * @param decimals Number of decimal places (default: 2)
   * @returns Formatted quantity string
   */
  formatQuantity(value: number | null | undefined, decimals: number = 2): string {
    if (value === null || value === undefined) return '-';
    return value.toFixed(decimals);
  }

  /**
   * Calculate total value for an order line
   *
   * @param quantity Quantity
   * @param price Unit price
   * @param discount Discount percentage (0-100)
   * @returns Total value after discount
   */
  calculateLineTotal(
    quantity: number | null | undefined,
    price: number | null | undefined,
    discount: number | null | undefined
  ): number {
    if (!quantity || !price) return 0;
    const subtotal = quantity * price;
    const discountAmount = discount ? (subtotal * discount) / 100 : 0;
    return subtotal - discountAmount;
  }

  /**
   * Calculate total value for an entire order
   *
   * @param order Order history DTO with lines
   * @returns Total order value
   */
  calculateOrderTotal(order: POrderHistoryDto): number {
    return order.lines.reduce((total, line) => {
      return total + this.calculateLineTotal(line.quantity, line.price, 0);
    }, 0);
  }

  /**
   * Calculate completion percentage for an order
   *
   * @param totalQuantity Total quantity ordered
   * @param totalDelivered Total quantity delivered
   * @returns Completion percentage (0-100)
   */
  calculateCompletionPercentage(
    totalQuantity: number,
    totalDelivered: number
  ): number {
    if (totalQuantity === 0) return 0;
    return Math.min(Math.round((totalDelivered / totalQuantity) * 100), 100);
  }

  // getStatusBadgeClass fjernet 2026-07-02 (WS3): status vises via StatusBadge/statusPresentation
  // (Model A, farge fra statusColor). Ingen kallere igjen etter WS1.

  /**
   * Check if an order is completed
   *
   * @param order Order history DTO
   * @returns True if order is fully delivered
   */
  isOrderCompleted(order: POrderHistoryDto): boolean {
    return order.totalQuantity > 0 && order.totalQuantity === order.totalDelivered;
  }

  /**
   * Check if an order line is completed
   *
   * @param line Order history line DTO
   * @returns True if line is fully delivered
   */
  isLineCompleted(line: { quantity?: number; quantityDelivered?: number }): boolean {
    const qty = line.quantity || 0;
    const delivered = line.quantityDelivered || 0;
    return qty > 0 && qty === delivered;
  }

  /**
   * Get summary statistics for a list of orders
   *
   * @param orders Array of order history DTOs
   * @returns Summary statistics object
   */
  getSummaryStatistics(orders: POrderHistoryDto[]): {
    totalOrders: number;
    totalLines: number;
    totalQuantity: number;
    totalDelivered: number;
    completedOrders: number;
    averageCompletionPercentage: number;
    totalTransactions: number;
  } {
    const stats = {
      totalOrders: orders.length,
      totalLines: 0,
      totalQuantity: 0,
      totalDelivered: 0,
      completedOrders: 0,
      averageCompletionPercentage: 0,
      totalTransactions: 0
    };

    orders.forEach(order => {
      stats.totalLines += order.totalLines;
      stats.totalQuantity += order.totalQuantity;
      stats.totalDelivered += order.totalDelivered;
      stats.totalTransactions += order.totalTransactions;
      if (this.isOrderCompleted(order)) {
        stats.completedOrders++;
      }
    });

    if (stats.totalOrders > 0) {
      stats.averageCompletionPercentage = Math.round(
        (stats.completedOrders / stats.totalOrders) * 100
      );
    }

    return stats;
  }
}

/**
 * Singleton instance of PurchaseOrderHistoryService
 * Use this exported instance throughout the application
 */
export const purchaseOrderHistoryService = new PurchaseOrderHistoryService();
